import { getSpeciesProfile, normalizeSpeciesSlug, tierDisplayName } from './core';

/**
 * @param {object} ctx
 * @param {number|null} ctx.ageMonths
 * @param {string|null|undefined} ctx.sex
 * @param {boolean|null} ctx.weaned
 * @param {boolean} ctx.castrated
 * @param {boolean} ctx.lactating
 * @param {boolean} ctx.breedingOpen
 */
function ageClassRuleMatches(rule, ctx) {
  const { ageMonths, sex, weaned, castrated, lactating, breedingOpen } = ctx;

  if (rule.sex && rule.sex !== sex) return false;
  if (rule.min_months != null && (ageMonths == null || ageMonths < rule.min_months)) return false;
  if (rule.max_months != null && (ageMonths == null || ageMonths > rule.max_months)) return false;
  if (rule.weaned === true && weaned !== true) return false;
  if (rule.not_weaned === true && weaned === true) return false;
  if (rule.castrated === true && !castrated) return false;
  if (rule.intact === true && castrated) return false;
  if (rule.lactating === true && !lactating) return false;
  if (rule.breeding_open === true && !breedingOpen) return false;

  return true;
}

function resolveAgeClassRuleLabel(rule, profiles, slug, tn) {
  if (rule.prefix && rule.tier) {
    return `${rule.prefix}${tn(rule.tier, rule.fallback || '')}`;
  }
  if (rule.tier) {
    return tn(rule.tier, rule.name || rule.fallback || '');
  }
  if (rule.name) {
    return rule.name;
  }
  return '';
}

/**
 * @param {list<array>} rules
 */
function firstMatchingAgeClassLabel(rules, ctx, profiles, slug, tn) {
  if (!rules?.length) return null;
  for (const rule of rules) {
    if (ageClassRuleMatches(rule, ctx)) {
      const label = resolveAgeClassRuleLabel(rule, profiles, slug, tn);
      if (label) return label;
    }
  }
  return null;
}

/**
 * @param {Record<string, unknown>|null|undefined} genericConfig
 */
function computeGenericAgeClass(input, genericConfig) {
  const { ageMonths, sex, weaned, castrated, breedingStatus, lactating } = input;
  const parts = [];

  if (genericConfig?.parts && ageMonths != null) {
    for (const part of genericConfig.parts) {
      const min = part.min_months ?? 0;
      const max = part.max_months ?? 999;
      if (ageMonths >= min && ageMonths <= max) {
        parts.push(part.label);
        break;
      }
    }
  }

  if (sex === 'male') parts.push(castrated ? 'Khasi' : 'Intact Male');
  if (sex === 'female') parts.push('Female');
  if (weaned === true) parts.push('Weaned');
  if (weaned === false) parts.push('Unweaned');
  if (sex === 'female' && breedingStatus) parts.push(String(breedingStatus));
  if (sex === 'female' && lactating) parts.push('Lactating');

  return parts.filter(Boolean).join(' • ');
}

/**
 * @param {object} input
 * @param {Record<string, unknown>|null|undefined} [profiles]
 * @param {Record<string, unknown>|null|undefined} [genericAgeClass]
 */
export function computeAgeClassFromRules(input, profiles = null, genericAgeClass = null) {
  const {
    ageMonths,
    categoryName,
    sex,
    weaned,
    castrated,
    breedingStatus,
    lactating,
  } = input;

  if (!sex && ageMonths === null && !weaned && !castrated && !breedingStatus && !lactating) {
    return '';
  }

  const slug = normalizeSpeciesSlug(categoryName);
  const profile = profiles ? getSpeciesProfile(profiles, slug) : null;
  const tn = (tier, fallback) => tierDisplayName(profiles, slug, tier, fallback);

  if (!slug) {
    return computeGenericAgeClass(input, genericAgeClass);
  }

  const rules = profile?.age_class;
  if (!rules) {
    if (sex === 'male') return castrated ? 'Castrated Male' : 'Adult Male';
    if (sex === 'female') return lactating ? 'Lactating Female' : 'Adult Female';
    return '';
  }

  const ctx = {
    ageMonths,
    sex,
    weaned,
    castrated: !!castrated,
    lactating: !!lactating,
    breedingOpen:
      String(breedingStatus || '').toLowerCase() === 'open'
      || String(breedingStatus || '').toLowerCase() === 'exposed'
      || String(breedingStatus || '').toLowerCase() === 'open_for_breeding',
  };

  if (ageMonths != null && rules.juvenile) {
    const juvenile = firstMatchingAgeClassLabel(rules.juvenile, ctx, profiles, slug, tn);
    if (juvenile) return juvenile;
  }

  if (sex === 'female' && rules.female) {
    const female = firstMatchingAgeClassLabel(rules.female, ctx, profiles, slug, tn);
    if (female) return female;
  }

  if (sex === 'male' && rules.male) {
    const male = firstMatchingAgeClassLabel(rules.male, ctx, profiles, slug, tn);
    if (male) return male;
  }

  return '';
}
